// IMPORTAÇÃO de clientes APP-ONLY a partir de uma planilha de CNPJs que NÃO estão no Sankhya.
// Cria cada linha como cliente do app vinculado a UM vendedor (por CODVEND), marcado
// `sankhya.status = 'nao_cadastrado'` — a marca (a) mostra o badge "Não cadastrado no Sankhya" no
// cadastro e (b) faz o gatilho `syncClienteSankhya` PULAR o envio ao ERP (ver functions/index.js).
// Sem CODPARC → o "Sincronizar Clientes" nunca os desativa (a desativação só varre quem tem codparc) e
// nunca os ADOTA por CNPJ (o sync trata `nao_cadastrado` como não-livre — ver functions/index.js).
// Dedup por CNPJ (14 dígitos): pula quem já existe no app. Espelha o shape do cadastro manual (AddClientScreen).
import * as XLSX from 'xlsx';
import { collection, getDocs, addDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

const soDigitos = (s: unknown): string => String(s ?? '').replace(/\D/g, '');
// CNPJ SEMPRE com 14 dígitos: recupera o zero à esquerda perdido quando a coluna vem como NÚMERO no Excel
// (ex.: 01234567000199 viraria o inteiro 1234567000199). Padroniza os dois lados do dedup e a gravação.
const normCnpj = (v: unknown): string => { const d = soDigitos(v); return d && d.length <= 14 ? d.padStart(14, '0') : d; };
// Normaliza cabeçalho: sem acento, minúsculo, sem espaço nas pontas ('Razão Social' == 'Razao Social').
const normKey = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

export interface ResumoImportAppOnly {
  vendedor: string;      // "1001 - Jordana Barboza"
  totalPlanilha: number;
  criados: number;
  pulados: number;       // CNPJ já existia no app (ou repetido na planilha)
  semCnpj: number;       // linha sem CNPJ válido
  semNome: number;       // linha sem Razão Social (name é obrigatório)
}

export async function importarClientesAppOnly(file: File, codvend: string): Promise<ResumoImportAppOnly> {
  const cv = soDigitos(codvend);
  if (!cv) throw new Error('Informe o CODVEND do vendedor.');

  // 1) Resolve o vendedor (uid + nome) pelo CODVEND (tenta número e string).
  let uSnap = await getDocs(query(collection(db, 'users'), where('codVendSankhya', '==', Number(cv))));
  if (uSnap.empty) uSnap = await getDocs(query(collection(db, 'users'), where('codVendSankhya', '==', cv)));
  if (uSnap.empty) throw new Error(`Nenhum vendedor com CODVEND ${cv} encontrado (cadastre-o na aba Vendedores).`);
  const uid = uSnap.docs[0].id;
  const nome = (uSnap.docs[0].data() as any).name || '';

  // 2) Lê a planilha (1ª aba) e normaliza os cabeçalhos (sem acento/caixa) — casa 'Razão Social', 'Município' etc.
  const wb = XLSX.read(new Uint8Array(await file.arrayBuffer()), { type: 'array' });
  const brutos: any[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
  if (!brutos.length) throw new Error('Planilha vazia.');
  const rows = brutos.map((r) => { const o: Record<string, unknown> = {}; for (const k in r) o[normKey(k)] = r[k]; return o; });
  // Valor da 1ª chave (normalizada) que tiver conteúdo — aceita variações (ex.: 'telefones'/'telefone').
  const g = (r: Record<string, unknown>, ...keys: string[]): string => {
    for (const k of keys) { const v = r[k]; if (v != null && String(v).trim() !== '') return String(v).trim(); }
    return '';
  };
  // Cabeçalhos obrigatórios: falha ALTO em vez de criar clientes com campos vazios em silêncio.
  const cab = new Set(Object.keys(rows[0]));
  if (!cab.has('cnpj')) throw new Error('A planilha precisa da coluna "CNPJ".');
  if (!cab.has('razao social')) throw new Error('A planilha precisa da coluna "Razao Social".');

  // 3) Dedup: CNPJs já existentes no app, normalizados a 14 dígitos.
  const cliSnap = await getDocs(collection(db, 'clients'));
  const existentes = new Set<string>();
  cliSnap.forEach((d) => { const c = normCnpj((d.data() as any).cnpj); if (c) existentes.add(c); });

  // 4) Cria os app-only (pula duplicados, linhas sem CNPJ e linhas sem Razão Social).
  let criados = 0; let pulados = 0; let semCnpj = 0; let semNome = 0;
  const vistos = new Set<string>();
  for (const r of rows) {
    const cnpj = normCnpj(r['cnpj']);
    if (!cnpj) { semCnpj++; continue; }
    if (existentes.has(cnpj) || vistos.has(cnpj)) { pulados++; continue; }
    const name = g(r, 'razao social');
    if (!name) { semNome++; continue; } // name é obrigatório (Client.name) — não cria registro vazio
    vistos.add(cnpj);
    const tel = g(r, 'telefones', 'telefone').split(/[;,/]+/)[0].trim();
    await addDoc(collection(db, 'clients'), {
      name,
      tradeName: g(r, 'nome fantasia') || null,
      cnpj,
      stateRegistration: null,
      phone: tel || null,
      email: g(r, 'e-mail', 'email') || null,
      address: {
        street: g(r, 'logradouro'),
        number: g(r, 'numero'),
        complement: g(r, 'complemento') || null,
        neighborhood: g(r, 'bairro'),
        city: g(r, 'municipio'),
        state: g(r, 'uf').toUpperCase(),
        zipCode: soDigitos(r['cep']),
      },
      notes: null,
      active: true,
      // Marca app-only: badge no cadastro + trava do envio ao Sankhya (syncClienteSankhya pula este status).
      sankhya: { codvend: Number(cv), status: 'nao_cadastrado' },
      sellerId: uid,                       // vínculo com a carteira do vendedor (o app casa por sellerId)
      sellerName: `${cv} - ${nome}`,
      createdBy: null,                     // importação de sistema — sem "criador" no app
      createdByName: 'Importação (planilha)',
      createdAt: serverTimestamp(),
    });
    criados++;
  }
  return { vendedor: `${cv} - ${nome}`, totalPlanilha: rows.length, criados, pulados, semCnpj, semNome };
}
